import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button, Form, Input, message as antMessage } from 'antd'
import { CloseOutlined, FileOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { IChatProps } from './interface'
import { getChatFileName, isImageChatFile, normalizeChatFiles } from './utils'
import ChatImagePreview from './image-preview'
import {
  AttachButton,
  AttachInput,
  ChatBody,
  ChatFooter,
  ChatWrapper,
  FooterActions,
  FooterLeftActions,
  LoadingPreloader,
  MessageAttachmentLink,
  MessageAttachments,
  MessageDate,
  MessageRow,
  MessageText,
  SelectedFileInfo,
  SelectedFileItem,
  SelectedFileName,
  SelectedFileRemove,
  SelectedFiles,
  SelectedFileSize,
} from './styles'

const formatFileSize = (size: number): string => {
  if (!Number.isFinite(size) || size <= 0) {
    return '0 Б'
  }

  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} МБ`
  }

  if (size >= 1024) {
    return `${Math.round(size / 1024)} КБ`
  }

  return `${size} Б`
}

const mergeFiles = (prevFiles: File[], nextFiles: File[]): File[] => {
  const map = new Map<string, File>()

  prevFiles.forEach(file => {
    map.set(`${file.name}:${file.size}:${file.lastModified}`, file)
  })

  nextFiles.forEach(file => {
    map.set(`${file.name}:${file.size}:${file.lastModified}`, file)
  })

  return Array.from(map.values())
}

function AppChat({ data, form, onSubmit, onMessageRead }: IChatProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const chatBodyRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const onMessageReadRef = useRef(onMessageRead)
  const hasInitialScrollDoneRef = useRef(false)
  onMessageReadRef.current = onMessageRead

  const state = useCallback(() => {
    if (data === undefined) return 'loading'
    if (data?.length === 0) return 'empty'
    return 'normal'
  }, [data])

  const messageValue = Form.useWatch('message', form)
  const hasPayload = (messageValue || '').trim() !== '' || selectedFiles.length > 0

  useLayoutEffect(() => {
    const container = chatBodyRef.current
    if (!container || !data || data.length === 0) return
    if (hasInitialScrollDoneRef.current) return

    const rafId = requestAnimationFrame(() => {
      hasInitialScrollDoneRef.current = true
      const firstUnread = container.querySelector<HTMLElement>('[data-unread-id]')
      if (firstUnread) {
        container.scrollTop = Math.max(0, firstUnread.offsetTop - 8)
      } else {
        container.scrollTop = container.scrollHeight
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [data])

  useEffect(() => {
    const container = chatBodyRef.current
    const targets = container?.querySelectorAll<HTMLElement>('[data-unread-id]') ?? []
    if (!container || !onMessageReadRef.current || targets.length === 0) return

    let observer: IntersectionObserver | null = null
    const rafId = requestAnimationFrame(() => {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return
            const id = entry.target.getAttribute('data-unread-id')
            const numId = parseInt(id || '', 10)
            if (numId) onMessageReadRef.current?.(numId)
            observer?.unobserve(entry.target)
          })
        },
        { root: container, rootMargin: '0px', threshold: 0.1 },
      )
      targets.forEach(el => observer?.observe(el))
    })

    return () => {
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }, [data])

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    if (!files.length) {
      return
    }

    setSelectedFiles(prev => mergeFiles(prev, files))
    event.target.value = ''
  }, [])

  const handleRemoveFile = useCallback((targetFile: File) => {
    setSelectedFiles(prev =>
      prev.filter(
        file =>
          !(
            file.name === targetFile.name &&
            file.size === targetFile.size &&
            file.lastModified === targetFile.lastModified
          ),
      ),
    )
  }, [])

  const handleSubmit = useCallback(async () => {
    const message = (form.getFieldValue('message') || '').trim()

    if (!message && selectedFiles.length === 0) {
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit({
        message,
        files: selectedFiles,
      })
      form.resetFields()
      setSelectedFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error && error.message ? error.message : 'Не удалось отправить сообщение'

      antMessage.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }, [form, onSubmit, selectedFiles])

  return (
    <ChatWrapper>
      <ChatBody ref={chatBodyRef} state={state()}>
        {data === undefined && <LoadingPreloader>Загрузка...</LoadingPreloader>}
        {data?.length === 0 && <LoadingPreloader>Нет сообщений</LoadingPreloader>}
        {data &&
          data.length > 0 &&
          [...data].reverse().map(item => {
            const isOutgoing = Boolean(item.manager_id)
            const isIncoming = !isOutgoing
            const shouldAutoRead = isIncoming && !item.is_read
            const attachments = normalizeChatFiles(item.files)

            return (
              <div key={item.id} {...(shouldAutoRead && { 'data-unread-id': String(item.id) })}>
                <MessageRow $left={isIncoming}>
                  <MessageText $incoming={isIncoming} $is_read={item.is_read}>
                    {item.msg}
                    {attachments.length > 0 && (
                      <MessageAttachments>
                        {attachments.map(fileUrl =>
                          isImageChatFile(fileUrl) ? (
                            <ChatImagePreview
                              key={fileUrl}
                              imageUrl={fileUrl}
                              alt={getChatFileName(fileUrl)}
                            />
                          ) : (
                            <MessageAttachmentLink
                              key={fileUrl}
                              href={fileUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <FileOutlined />
                              <span>{getChatFileName(fileUrl)}</span>
                            </MessageAttachmentLink>
                          ),
                        )}
                      </MessageAttachments>
                    )}
                    <MessageDate>{dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}</MessageDate>
                  </MessageText>
                </MessageRow>
              </div>
            )
          })}
      </ChatBody>
      <ChatFooter>
        <Form form={form} onFinish={handleSubmit} autoComplete="off">
          <Form.Item name="message" style={{ marginBottom: 12 }}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>
          <FooterActions>
            <FooterLeftActions>
              <AttachButton
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Прикрепить файлы"
                aria-label="Прикрепить файлы"
              >
                <PaperClipOutlined />
              </AttachButton>
              <AttachInput
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                hidden
                tabIndex={-1}
                aria-hidden="true"
              />
            </FooterLeftActions>
            <Button type="primary" htmlType="submit" disabled={!hasPayload || isSubmitting}>
              <SendOutlined />
              Отправить
            </Button>
          </FooterActions>
          {selectedFiles.length > 0 && (
            <SelectedFiles>
              {selectedFiles.map(file => {
                const fileKey = `${file.name}:${file.size}:${file.lastModified}`

                return (
                  <SelectedFileItem key={fileKey}>
                    <SelectedFileInfo>
                      <FileOutlined />
                      <SelectedFileName>{file.name}</SelectedFileName>
                      <SelectedFileSize>{formatFileSize(file.size)}</SelectedFileSize>
                    </SelectedFileInfo>
                    <SelectedFileRemove
                      type="button"
                      onClick={() => handleRemoveFile(file)}
                      title="Удалить файл"
                      aria-label="Удалить файл"
                    >
                      <CloseOutlined />
                    </SelectedFileRemove>
                  </SelectedFileItem>
                )
              })}
            </SelectedFiles>
          )}
        </Form>
      </ChatFooter>
    </ChatWrapper>
  )
}

AppChat.displayName = 'AppChat'

export default AppChat
